import { Fragment } from "react";

import { ActivityFillCell } from "#/components/ui/activity-fill-cell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card";
import { Infotip, InfotipContent, InfotipProvider, InfotipTrigger } from "#/components/ui/infotip";
import { Skeleton } from "#/components/ui/skeleton";
import { profileActivityFillLabel, resolveActivityFillFromCompletion } from "#/lib/calendarHeatmap";
import { cn } from "#/lib/utils";
import {
  loadProfileActivity,
  type ProfileActivityData,
  type ProfileActivityDay,
} from "#/server/game/profileActivity";

import { ActivityFillLegend } from "./-activityFill.legend";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function monthLabelsForWeeks({
  weeks,
  endDateStr,
}: {
  weeks: Array<{ days: ProfileActivityDay[] }>;
  endDateStr: string;
}): string[] {
  const labels: string[] = [];
  let lastMonth = "";
  for (const week of weeks) {
    const firstDated = week.days.find((day) => day.date && day.date <= endDateStr);
    const month = firstDated?.date?.slice(0, 7) ?? "";
    if (month && month !== lastMonth) {
      labels.push(month);
      lastMonth = month;
    } else {
      labels.push("");
    }
  }
  return labels;
}

function formatMonthLabel(monthKey: string): string {
  if (!monthKey) return "";
  const year = Number(monthKey.slice(0, 4));
  const monthIndex = Number(monthKey.slice(5, 7)) - 1;
  return new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, monthIndex, 1)),
  );
}

function activityDayFill(day: ProfileActivityDay) {
  if (!day.date) return null;
  return resolveActivityFillFromCompletion({
    date: day.date,
    standardCompleted: day.standardCompleted,
    premiumCompleted: day.premiumCompleted,
  });
}

function activityDayTitle(day: ProfileActivityDay): string {
  if (!day.date) return "";
  const fill = activityDayFill(day);
  if (!fill) return "";
  return `${day.date}: ${profileActivityFillLabel(fill)}`;
}

function ActivityDayCell({ day }: { day: ProfileActivityDay }) {
  if (!day.date) {
    return <div className="aspect-square min-h-2.5 min-w-2.5 w-full" aria-hidden />;
  }

  const label = activityDayTitle(day);

  return (
    <Infotip>
      <InfotipTrigger
        aria-label={label}
        className="flex aspect-square min-h-2.5 min-w-2.5 w-full cursor-default"
      >
        <ActivityFillCell fill={activityDayFill(day) ?? "empty"} size="full" />
      </InfotipTrigger>
      <InfotipContent side="top">{label}</InfotipContent>
    </Infotip>
  );
}

function activityGridColumns(weekCount: number): string {
  return `1.5rem repeat(${weekCount}, minmax(0.625rem, 1fr))`;
}

function ActivityGraphGrid({
  todayStr,
  weeks,
}: {
  todayStr: string;
  weeks: Awaited<ReturnType<typeof loadProfileActivity>>["weeks"];
}) {
  const monthLabels = monthLabelsForWeeks({ weeks, endDateStr: todayStr });

  return (
    <InfotipProvider delay={300}>
      <div
        className="grid w-full gap-[3px]"
        style={{
          gridTemplateColumns: activityGridColumns(weeks.length),
          gridTemplateRows: "auto 0.5rem repeat(7, minmax(0.625rem, 1fr))",
        }}
      >
        <div aria-hidden />
        {monthLabels.map((monthKey, weekIndex) => (
          <span
            key={`month-${weekIndex}`}
            className="flex min-h-2.5 items-end text-[10px] leading-none text-muted-foreground"
          >
            {formatMonthLabel(monthKey)}
          </span>
        ))}
        <div aria-hidden />
        {weeks.map((_, weekIndex) => (
          <div key={`month-gap-${weekIndex}`} aria-hidden />
        ))}
        {WEEKDAY_LABELS.map((label, dayIndex) => (
          <Fragment key={label}>
            <span
              className={cn(
                "flex min-h-2.5 items-center text-[9px] leading-none text-muted-foreground",
                dayIndex % 2 === 1 && "opacity-0",
              )}
            >
              {label}
            </span>
            {weeks.map((week, weekIndex) => (
              <ActivityDayCell key={`${weekIndex}-${dayIndex}`} day={week.days[dayIndex]} />
            ))}
          </Fragment>
        ))}
      </div>
    </InfotipProvider>
  );
}

function ActivityGraphBody({
  todayStr,
  weeks,
}: {
  todayStr: string;
  weeks: Awaited<ReturnType<typeof loadProfileActivity>>["weeks"];
}) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <div className="w-full min-w-0 overflow-x-auto">
        <ActivityGraphGrid todayStr={todayStr} weeks={weeks} />
      </div>
      <ActivityFillLegend className="self-end" />
    </div>
  );
}

function ProfileActivityGraphSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-3 w-48" />
      <Skeleton className="h-[108px] w-full" />
    </div>
  );
}

function ProfileActivityCardHeader() {
  return (
    <CardHeader>
      <CardTitle>Activity</CardTitle>
      <CardDescription>Games you completed over the past year.</CardDescription>
    </CardHeader>
  );
}

export function ProfileActivityCardSkeleton() {
  return (
    <Card>
      <ProfileActivityCardHeader />
      <CardContent>
        <ProfileActivityGraphSkeleton />
      </CardContent>
    </Card>
  );
}

export function ProfileActivityCard({ activity }: { activity: ProfileActivityData }) {
  return (
    <Card>
      <ProfileActivityCardHeader />
      <CardContent>
        <ActivityGraphBody todayStr={activity.todayStr} weeks={activity.weeks} />
      </CardContent>
    </Card>
  );
}
